// MIT License - Copyright (c) 2026 Doug McNall

/**
 * OverlayGuard
 *
 * Tracks whether a full-screen overlay (e.g. AlienEncounterModal) is
 * currently open, so global keyboard listeners — tab hotkeys, cheat
 * codes, modifier shortcuts — can suppress themselves while it's up.
 *
 * Deliberately NOT the same thing as UIContext's modal stack. Most of
 * this app's modals (AlienEncounterModal, ShipUpgradesModal, ArmoryModal,
 * etc.) are local useState portals rendered outside UIContext entirely —
 * isModalOpen() from useUI() can't see them. A module-level counter here
 * is the cheap way to get a signal that covers overlays regardless of
 * where they keep their open/closed state, without migrating every modal
 * in the app onto UIContext.
 *
 * Currently registered by: AlienEncounterModal (the component whose
 * "Talk to alien" text input doesn't hold focus reliably, letting
 * single-letter hotkeys like 'c' leak through to global listeners).
 * Other local-state modals have the same architectural gap but aren't
 * registered yet — add a register call to any modal that turns out to
 * leak hotkeys the same way.
 *
 * Usage in an overlay component:
 *   useEffect(() => {
 *     const release = OverlayGuard.open();
 *     return release;
 *   }, []);
 */
let openCount = 0;

export const openOverlay = () => {
  openCount += 1;
  return () => {
    openCount = Math.max(0, openCount - 1);
  };
};

export const isOverlayOpen = () => openCount > 0;

const OverlayGuard = { open: openOverlay, isOpen: isOverlayOpen };

export default OverlayGuard;
